import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { useResetPasswordMutation } from '../../services/authApi';

const ResetPassword = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resetPassword, { isLoading, error }] = useResetPasswordMutation();
  const [passwordReset, setPasswordReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthLabel = (strength) => {
    switch (strength) {
      case 0:
      case 1:
        return { label: 'Very Weak', color: 'bg-red-500' };
      case 2:
        return { label: 'Weak', color: 'bg-orange-500' };
      case 3:
        return { label: 'Fair', color: 'bg-yellow-500' };
      case 4:
        return { label: 'Good', color: 'bg-blue-500' };
      case 5:
        return { label: 'Strong', color: 'bg-green-500' };
      default:
        return { label: 'Very Weak', color: 'bg-red-500' };
    }
  };

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Please confirm your password'),
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log('Submitting reset password:', { token, password: values.password });

      if (!token) {
        console.error('No token available');
        return;
      }

      try {
        const result = await resetPassword({
          token,
          password: values.password,
        }).unwrap();
        setPasswordReset(true);
      } catch (error) {
        console.error('Reset password failed:', error);
        // Error will be displayed via the error state from the mutation
      }
    },
  });

  const passwordRequirements = [
    { text: 'At least 8 characters', met: formik.values.password.length >= 8 },
    { text: 'One lowercase letter', met: /[a-z]/.test(formik.values.password) },
    { text: 'One uppercase letter', met: /[A-Z]/.test(formik.values.password) },
    { text: 'One number', met: /[0-9]/.test(formik.values.password) },
    { text: 'One special character', met: /[^A-Za-z0-9]/.test(formik.values.password) }
  ];

  const strength = getPasswordStrengthLabel(passwordStrength);

  useEffect(() => {
    // Get token from URL params
    const tokenFromUrl = searchParams.get('token');
    console.log('Token from URL:', tokenFromUrl);
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      // If no token in URL, redirect to forgot password
      console.log('No token in URL, redirecting to forgot password');
      navigate('/forgot-password');
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    // Update password strength when password changes
    setPasswordStrength(calculatePasswordStrength(formik.values.password));
  }, [formik.values.password]);

  if (passwordReset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 animate-fade-in">
        <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
          {/* Header */}
          <div className="text-center animate-fade-in-up">
            {/* Logo */}
            <div className="flex justify-center mb-2">
              <img
                src="/assets/logo.png"
                alt="Innovative HRMS Logo"
                className="h-12 w-auto sm:h-16 md:h-20 lg:h-36 object-contain"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <div className="hidden text-2xl sm:text-3xl md:text-4xl font-bold text-primary-600">
                Innovative HRMS
              </div>
            </div>

            <div className="flex justify-center mb-4">
              <CheckCircleIcon className="h-16 w-16 text-success-500" />
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-secondary-900">
              Password Reset Successful
            </h2>
            <p className="mt-2 text-sm sm:text-base text-secondary-600 px-4">
              Your password has been successfully reset.
            </p>
            <p className="mt-1 text-sm text-secondary-500 px-4">
              You can now sign in with your new password.
            </p>
          </div>

          {/* Success Card */}
          <div className="card card-glass animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            <div className="card-body p-4 sm:p-6 md:p-8">
              <button
                onClick={() => navigate('/login')}
                className="btn-primary w-full py-3 sm:py-3.5 text-sm sm:text-base font-medium group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center">
                  Sign In
                  <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </span>
                <div className="absolute inset-0 bg-primary-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 animate-fade-in">
      <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center animate-fade-in-up">
          {/* Logo */}
          <div className="flex justify-center mb-2">
            <img
              src="/assets/logo.png"
              alt="Innovative HRMS Logo"
              className="h-12 w-auto sm:h-16 md:h-20 lg:h-36 object-contain"
              onError={(e) => {
                // Fallback to text logo if image fails to load
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            <div className="hidden text-2xl sm:text-3xl md:text-4xl font-bold text-primary-600">
              Innovative HRMS
            </div>
          </div>

          <h2 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-display font-bold text-secondary-900">
            Reset Your Password
          </h2>
          <p className="mt-2 text-sm sm:text-base text-secondary-600 px-4">
            Enter your new password below.
          </p>
        </div>

        {/* Reset Password Form */}
        <div className="card card-glass animate-fade-in-up" style={{animationDelay: '0.2s'}}>
          <div className="card-body p-4 sm:p-6 md:p-8">
            <form className="space-y-4 sm:space-y-6" onSubmit={formik.handleSubmit}>
              {/* Error Message */}
              {error && (
                <div className="rounded-lg bg-error-50 border border-error-200 p-4 animate-fade-in">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-error-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-error-800">{error?.data?.message || 'Failed to reset password. Please try again.'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Password Field */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.3s'}}>
                <label htmlFor="password" className="form-label">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    className={`form-input pl-10 pr-10 ${
                      formik.touched.password && formik.errors.password
                        ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                        : ''
                    }`}
                    placeholder="Enter your new password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      className="text-secondary-400 hover:text-secondary-600 focus:outline-none focus:text-secondary-600 transition-colors duration-200"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="form-error">{formik.errors.password}</p>
                )}

                {/* Password Strength Indicator */}
                {formik.values.password && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-secondary-600">Password Strength:</span>
                      <span className={`text-sm font-medium ${
                        strength.label === 'Strong' ? 'text-green-600' :
                        strength.label === 'Good' ? 'text-blue-600' :
                        strength.label === 'Fair' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {strength.label}
                      </span>
                    </div>
                    <div className="w-full bg-secondary-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${strength.color}`}
                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.4s'}}>
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`form-input pl-10 pr-10 ${
                      formik.touched.confirmPassword && formik.errors.confirmPassword
                        ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                        : ''
                    }`}
                    placeholder="Confirm your new password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      className="text-secondary-400 hover:text-secondary-600 focus:outline-none focus:text-secondary-600 transition-colors duration-200"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <p className="form-error">{formik.errors.confirmPassword}</p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-white/50 rounded-lg p-4 animate-fade-in-up" style={{animationDelay: '0.45s'}}>
                <h4 className="text-sm font-medium text-secondary-900 mb-3">Password Requirements:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <div className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center ${
                        req.met ? 'bg-green-500' : 'bg-secondary-300'
                      }`}>
                        {req.met && (
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className={req.met ? 'text-green-700' : 'text-secondary-600'}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.5s'}}>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-3 sm:py-3.5 text-sm sm:text-base font-medium group relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Resetting...
                      </>
                    ) : (
                      <>
                        Reset Password
                        <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-primary-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </button>
              </div>

              {/* Back to Login */}
              <div className="text-center animate-fade-in-up" style={{animationDelay: '0.6s'}}>
                <p className="text-sm text-secondary-600">
                  Remember your password?{' '}
                  <Link
                    to="/login"
                    className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center animate-fade-in-up" style={{animationDelay: '0.7s'}}>
          <p className="text-sm text-secondary-600">
            © {new Date().getFullYear()} Innovative HRMS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;